import React, { useEffect, useState } from 'react';
import VideoPopup from '../OnboardingModal/VideoPopup';
import RoundedModal from '../../../../components/roundedModal/roundedModal';
import useTranslation from '../../../../common/hooks/useTranslation';
import StyledButton from '../../../../components/form-control/styledButton/styledButton';
import MarkedText from '../../../../components/MarkedText/MarkedText';
import styles from './WelcomeMessagePopup.module.css';
import useAnalyticsTracker from '@hooks/useAnalyticsTracker';
import { useDispatch } from 'react-redux';
import { setWelcomeMessagePopupVisible } from '@src/features/interface/interfaceSlice';

interface Props {
	visible: boolean;
	url: string;
	onVideoPlayed: () => void;
	onModalClosed: () => void;
}


const WelcomeMessagePopup = (props: Props): React.JSX.Element | null => {
	const { trackEvent } = useAnalyticsTracker('onboarding');
	const t = useTranslation('Luncher.WelcomePopup');
	const [videoWatched, setVideoWatched] = useState(false);
	const [showVideo, setShowVideo] = useState(false);
	const dispatch = useDispatch();

	const handleVideoPlayed = (): void => {
		setVideoWatched(true);
	};
	
	const handleOkPressed = () => {
		trackEvent('welcome_message_get_started_button_click');
		props.onVideoPlayed();
		props.onModalClosed();
	};

	const CustomFooter = (): React.JSX.Element => {
		return (
			<div className={styles.ModalFooter}>
				<div className={styles.FooterText}>
					{t('FooterMessage', 'Watch this 1 min video to know what you can do with Kemu!')}
				</div>
				<StyledButton
					color='primary'
					disabled={!videoWatched}
					title={t('GetStartedButton')}
					onClick={handleOkPressed}
				/>
			</div>
		);
	};

	
	// Workaround to avoid the video being shown before the modal is fully loaded, which triggers an error
	// in the vimeo library
	useEffect(() => {  
		let timeout: NodeJS.Timeout;
		if(props.visible) {
			timeout = setTimeout(() => {
				setShowVideo(true);
			}, 200);
		}
		
		// Make sure the hub api key modal is not shown
		dispatch(setWelcomeMessagePopupVisible(props.visible));
		return () => {
			clearTimeout(timeout);
		};
	}, [props.visible]);
	
	if(!props.visible) { return null; }

	return (
		<RoundedModal
			visible={props.visible}
			onAfterClosed={props.onModalClosed}
			onCancel={props.onModalClosed}
			closeOnMaskClick={false}
			centered={true}
			// Using markdown as it uses 'dangerouslySetInnerHTML' to allow empty space in the text
			title={<MarkedText text={t('Title', '🎉&nbsp;&nbsp;Welcome To Kemu !')} />}
			closable={true}
			customFooter={<CustomFooter />}
			bodyClassName={styles.ModalWrapper}
			width={'53.332vw'}
		>
			<div className={styles.VideoWrapper}>
				{showVideo ? (
					<div>
						<VideoPopup
							onVideoViewed={handleVideoPlayed}
							onClose={props.onModalClosed}
							wrapperClassName={styles.VideoWrapper}
							url={props.url}
						/>
					</div>
				) : (
					<div className={styles.VideoPlaceholder} />
				)}
			</div>
		</RoundedModal>
	);
};

export default WelcomeMessagePopup;
